import { useCallback, useEffect, useRef, useState } from 'react'

type LoadMoreFetcher<T> = (
  page: number,
  limit: number,
) => Promise<{ items: T[]; total: number }>

type UseLoadMoreListOptions<T> = {
  fetchPage: LoadMoreFetcher<T>
  limit?: number
  /** When any dependency changes, list resets to page 1. */
  resetDeps?: unknown[]
}

export function useLoadMoreList<T>({
  fetchPage,
  limit = 20,
  resetDeps = [],
}: UseLoadMoreListOptions<T>) {
  const [items, setItems] = useState<T[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fetchPageRef = useRef(fetchPage)
  fetchPageRef.current = fetchPage

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    setPage(1)
    try {
      const data = await fetchPageRef.current(1, limit)
      setItems(data.items)
      setTotal(data.total)
    } catch (e) {
      setItems([])
      setTotal(0)
      setError(e instanceof Error ? e.message : 'Failed to load list')
      throw e
    } finally {
      setLoading(false)
    }
  }, [limit])

  useEffect(() => {
    void reload().catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when filter deps change
  }, [reload, ...resetDeps])

  const loadMore = useCallback(async () => {
    if (loadingMore || loading || items.length >= total) return
    setLoadingMore(true)
    setError(null)
    try {
      const nextPage = page + 1
      const data = await fetchPageRef.current(nextPage, limit)
      setItems((prev) => [...prev, ...data.items])
      setTotal(data.total)
      setPage(nextPage)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load more')
    } finally {
      setLoadingMore(false)
    }
  }, [items.length, limit, loading, loadingMore, page, total])

  const hasMore = items.length < total

  const patchItem = useCallback((match: (item: T) => boolean, updater: (item: T) => T) => {
    setItems((prev) => prev.map((item) => (match(item) ? updater(item) : item)))
  }, [])

  return {
    items,
    total,
    loading,
    loadingMore,
    error,
    hasMore,
    reload,
    loadMore,
    patchItem,
  }
}
